/**
 * postMessage event types for iframe communication
 */

export type PostMessageEventType =
  | 'CARD_FORM_READY'
  | 'INIT_WIDGET'       // Phase 3 - parent sends API URL, bearer token, and order context
  | 'WIDGET_INITIALIZED' // Iframe → parent: token validated, form rendered, submit listener active
  | 'ORDER_CONTEXT'      // Legacy - parent sends order context to display form
  | 'CARD_FORM_SUBMIT'  // Legacy - kept for backwards compatibility
  | 'SUBMIT_CARD_FORM'  // New - parent triggers submission
  | 'CARD_FORM_VALIDATE'
  | 'CARD_FORM_VALIDATION_STATE'  // New - iframe sends validation state to parent
  | 'CARD_FORM_VALIDATION_ERROR'  // New - validation failed
  | 'CARD_FORM_ERROR'
  | 'CARD_FORM_SUCCESS'
  | 'CARD_FORM_RESIZE'
  | 'CARD_FORM_CANCEL';

export interface PostMessageEvent<T = unknown> {
  type: PostMessageEventType;
  payload: T;
  correlationId?: string;
  timestamp: string;
}

export interface CardFormReadyPayload {
  version: string;
  ready: boolean;
}

/**
 * Configuration for the submit button inside the widget
 */
export interface SubmitButtonConfig {
  /**
   * Whether to show the submit button inside the widget
   * @default true
   */
  show?: boolean;

  /**
   * Label text for the submit button
   * @default "Submit Payment"
   */
  label?: string;

  /**
   * Button background color (hex, rgb, rgba, or CSS color name)
   * @default "#000000"
   */
  color?: string;

  /**
   * Button foreground (text) color (hex, rgb, rgba, or CSS color name)
   * @default "#ffffff"
   */
  foregroundColor?: string;

  /**
   * Whether the button should be disabled initially
   * @default false
   */
  disabled?: boolean;
}

export interface InitWidgetPayload {
  apiUrl: string;
  kongBaseUrl: string;
  bearerToken: string;
  correlationId?: string;
  orderContext: {
    orderId: string;
    amount: number;
    currency: string;
    vendorId: string;
    customerId?: string;
    propertyId?: string;
    excludeCards?: string[]; // Card brand names to block, e.g. ["Amex", "Discover"]
  };
  /**
   * Optional submit button configuration
   * If not provided, button is shown by default
   */
  submitButton?: SubmitButtonConfig;
}

export interface CardFormSubmitPayload {
  orderId: string;
  amount: number;
  currency: string;
  vendorId: string;
}

export interface CardFormSuccessPayload {
  orderContext: {
    orderId: string;
    amount: number;
    currency: string;
    vendorId: string;
    customerId?: string;
    propertyId?: string;
  };
  encryptedPayload: {               // Object: {encryptedData, encryptedKey, iv, tag, timestamp}
    encryptedData: string;
    encryptedKey: string;
    iv: string;
    tag: string;
    timestamp: string;
  };
  keyVersion: string;              // Key version used for encryption (e.g. "v1")
  last4Digits: string;
  cardType: string;
  correlationId: string;
}

export type CardFormErrorCode =
  | 'INVALID_TOKEN'
  | 'VALIDATION_ERROR'
  | 'AUTH_ERROR'
  | 'ENCRYPTION_ERROR'
  |'NETWORK_ERROR';

export interface CardFormErrorPayload {
  error: string;
  errorCode?: CardFormErrorCode;
  validationErrors?: string[];
}

export interface CardFormResizePayload {
  height: number;
}

export interface CardFormValidationStatePayload {
  isValid: boolean;
  errors?: string[];
}

export interface CardFormValidationErrorPayload {
  errors: string[];
}

/**
 * Type guard for PostMessageEvent
 */
export function isPostMessageEvent(event: MessageEvent): event is MessageEvent<PostMessageEvent> {
  return (
    typeof event.data === 'object' &&
    event.data !== null &&
    'type' in event.data &&
    'payload' in event.data &&
    'timestamp' in event.data
  );
}
